import { useRef } from 'react';

/**
 * Returns a referentially stable callback which calls the most recent version
 * of the given callback.
 *
 * "referentially stable" means the identity of the returned callback will
 * never change. Including such a value in dependency arrays (see useEffect,
 * useMemo, useCallback) will not affect anything. Including such a value in
 * component props will never cause re-renders.
 *
 * @param callback The function the returned function will call.
 * @returns A referentially stable function that forwards calls to the most recent value of `callback`.
 */
// TODO: This should probably be replaced with `useEffectEvent` once it's available or `useEvent` from `react-use-event-hook`.
export function useStableCallback<T extends (...args: never) => unknown>(
	callback: T
): T {
	// Maintain a ref to the current callback instance.
	const callbackRef = useRef(callback);
	callbackRef.current = callback;

	// Create a wrapper callback that will never change.
	const stableCallbackRef = useRef(((...args) =>
		callbackRef.current(...args)) as T);

	return stableCallbackRef.current;
}
